import { forEach, get, has, isEqual, isFinite, isNumber, keyBy, map } from 'lodash';

import { PostProcessingActionCreated } from 'interfaces/postProcessingAction';

export const INSTANCE_COUNT_POSITIVITY_ACTION_NAME = 'get_instance_count_positivity_by_thresholding';
export const INSTANCE_COUNT_THRESHOLDS_INPUT_KEY = 'action_params.thresholds_by_instance';

export interface InstanceCountThresholdOption {
  id: string;
  label: string;
  instanceName: string;
  defaultThreshold: number;
}

export interface InstanceCountThresholdSyncResult {
  thresholds: Record<string, unknown>;
  autoManagedThresholds: Record<string, unknown>;
  changedInstanceNames: string[];
}

export const isValidInstanceCountThreshold = (threshold: unknown): threshold is number =>
  isNumber(threshold) && isFinite(threshold) && threshold >= 0;

export const getInstanceCountThresholdOptions = (options: unknown[]): InstanceCountThresholdOption[] =>
  map(
    keyBy(
      options.filter(
        (option): option is InstanceCountThresholdOption =>
          typeof option === 'object' &&
          option !== null &&
          typeof (option as InstanceCountThresholdOption).instanceName === 'string' &&
          isValidInstanceCountThreshold((option as InstanceCountThresholdOption).defaultThreshold)
      ),
      'instanceName'
    )
  );

// Keeps each probe's positivity threshold tracking its H-score bins (via defaultThreshold) unless the
// user has manually edited that probe's value away from the last value this sync itself applied. A
// probe with no recorded value yet is treated as auto-managed, so it gets the fresh default immediately.
export const syncInstanceCountThresholds = (
  currentThresholds: Record<string, unknown>,
  previousAutoAppliedThresholds: Record<string, unknown>,
  options: InstanceCountThresholdOption[]
): InstanceCountThresholdSyncResult => {
  const thresholds: Record<string, unknown> = {};
  const autoManagedThresholds: Record<string, unknown> = {};
  const changedInstanceNames: string[] = [];

  forEach(options, ({ instanceName, defaultThreshold }) => {
    const hasCurrentValue = has(currentThresholds, instanceName);
    const isAutoManaged =
      !hasCurrentValue || isEqual(currentThresholds[instanceName], previousAutoAppliedThresholds[instanceName]);

    if (!isAutoManaged) {
      thresholds[instanceName] = currentThresholds[instanceName];
      return;
    }

    thresholds[instanceName] = defaultThreshold;
    autoManagedThresholds[instanceName] = defaultThreshold;
    if (hasCurrentValue && !isEqual(currentThresholds[instanceName], defaultThreshold)) {
      changedInstanceNames.push(instanceName);
    }
  });

  return { thresholds, autoManagedThresholds, changedInstanceNames };
};

export const areInstanceCountThresholdsValid = (
  action: PostProcessingActionCreated,
  options: InstanceCountThresholdOption[]
): boolean => {
  if (options.length === 0) {
    return false;
  }

  const currentThresholds = get(action, INSTANCE_COUNT_THRESHOLDS_INPUT_KEY, {});
  return options.every(({ instanceName }) => isValidInstanceCountThreshold(currentThresholds[instanceName]));
};

export const shouldUpdateInstanceCountThresholds = (
  action: PostProcessingActionCreated,
  thresholds: Record<string, unknown>
): boolean => !isEqual(get(action, INSTANCE_COUNT_THRESHOLDS_INPUT_KEY, {}), thresholds);
